import React, {useState, useEffect, useContext} from 'react';
import SimpleReactValidator from 'simple-react-validator';
import Slide from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import BidmiiTextField from '../../../common/StyledComponents/BidmiiTextField';
import {NewProjectContext} from '../utils/NewProjectContext';
import ActionButtons from './ActionButtons';
import {CUSTOM_EVENT_NAMES, fireCustomEvent} from '../../../util/Analytics';
import {
  resetSecuredFetchRequest,
  securedFetchRequest,
} from '@bidmii/common/lib/util/FetchRequest';
import {firebaseAuth} from '../../../App';
import {signInAnonymously} from 'firebase/auth';
import {userState} from '../../../recoil/user/userAtom';
import {useRecoilValue} from 'recoil';
import * as Sentry from '@sentry/react';
import {API_URL} from '../../../constants';
import {apiRootState} from '../../../recoil/rootAtom';

interface IProps {
  changeStepHandler: (number: number) => void;
  forward: boolean;
  defaultValue: string;
}

const placeholders = [
  'Mow my lawn',
  'Yard Cleanup',
  'Renovate my kitchen',
  'Paint my living room walls',
  'Fix leaky plumbing',
  'Design my bedroom',
  'Build new fence in backyard',
  'Fix cracked concrete',
  'Patch hole in drywall',
  'Replace bathroom tiles',
  'Run gas line to BBQ',
  'Assemble furniture',
  'Take junk to dump',
  'Install smart doorbell',
  'Upgrade my laundry room',
  'Finish my basement',
  'Install egress windows',
  'Holiday Lighting',
];

const ProjectTitle = ({changeStepHandler, forward, defaultValue}: IProps) => {
  useEffect(() => {
    fireCustomEvent(CUSTOM_EVENT_NAMES.NEW_PROJECT_WIZARD_TITLE);
  }, []);

  const user = useRecoilValue(userState);
  const apiRoot = useRecoilValue(apiRootState);
  const {project, setProject, setNewProjectStep, homeDepot} =
    useContext(NewProjectContext);

  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [title, setTitle] = useState(defaultValue || '');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [validator] = useState(new SimpleReactValidator());
  const errors = {};

  useEffect(() => {
    const placeholderInterval = setInterval(() => {
      setPlaceholderIndex((prev) =>
        prev === placeholders.length - 1 ? 0 : prev + 1,
      );
    }, 2000);
    return () => clearInterval(placeholderInterval);
  }, []);

  const loginAnonymously = () => {
    signInAnonymously(firebaseAuth)
      .catch(function (err) {
        process.env.NODE_ENV === 'development' &&
          console.error('ANONYMOUS LOGIN FAIL');
        process.env.NODE_ENV === 'development' && console.error(err.code);
        process.env.NODE_ENV === 'development' && console.error(err.message);
        Sentry.withScope(function (scope) {
          scope.setTag('section', 'NewProjectStepOne.tsx loginAnonymously()');
          Sentry.captureException(err);
        });
      })
      .then((res) => {
        localStorage.setItem('isAnonymous', '1');
        localStorage.setItem('anonymousUid', (res as $TSFixMe).user?.uid);
        localStorage.setItem('userType', 'REQUESTER');
      });
  };

  const validateField = (field: $TSFixMe) => {
    if (!validator.fieldValid(field)) {
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      errors[field] = true;
      validator.showMessageFor(field);
      return;
    }
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    delete errors[field];
  };

  const handleSubmit = async (anonymous: boolean) => {
    if (!validator.allValid()) {
      ['title'].forEach((field) => {
        validateField(field);
      });
      return;
    }

    setDisabled(true);
    setLoading(true);

    const rfp: $TSFixMe = {
      enabled: true,
      title,
      description: project.description || '',
      privacy: 'anyone',
    };

    if (anonymous) {
      rfp.enabled = false;
    }
    process.env.NODE_ENV === 'development' && console.log('rfp :>> ', rfp);

    const url = project.uid
      ? `${API_URL}/rfps/${project.uid}`
      : // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
        apiRoot._links.rfps.href;
    securedFetchRequest(url, {
      method: project.uid ? 'PATCH' : 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(rfp),
    })
      .then((res) => res.json())
      .then((res) => {
        setLoading(false);
        setDisabled(false);
        localStorage.setItem('rfp', JSON.stringify(res));
        setProject(res);
        changeStepHandler(1);
      })
      .catch((err) => {
        setLoading(false);
        setDisabled(false);
        process.env.NODE_ENV === 'development' && console.error(err);

        Sentry.withScope(function (scope) {
          scope.setUser({
            uid: user?.uid,
            firstName: user?.firstName,
          });
          scope.setTag('section', 'create_user_step_2');
          Sentry.captureException(err);
        });
      });
  };

  const handleClickNext = () => {
    firebaseAuth.onAuthStateChanged((updatedUser) => {
      resetSecuredFetchRequest(
        updatedUser
          ? (forceRefresh) => updatedUser.getIdToken(forceRefresh)
          : null,
      );
      if (updatedUser !== null && updatedUser.isAnonymous) {
        handleSubmit(true);
      }
    });

    if (!user) {
      loginAnonymously();
    } else if (window.localStorage.getItem('isAnonymous')) {
      handleSubmit(true);
    } else {
      handleSubmit(false);
    }
  };

  const handleKeyDown = (event: $TSFixMe) => {
    if (event.keyCode === 13) {
      event.preventDefault(); // Ensure it is only this code that runs
      handleClickNext();
    }
  };

  return (
    <Slide in={true} direction={forward ? 'left' : 'right'} timeout={400}>
      <div style={{width: '100%', maxWidth: 450}}>
        <Typography variant="h6" fontSize={22} mb={3} fontWeight="bold">
          What would you like to get done?
        </Typography>
        <BidmiiTextField
          //label="Project title*"
          autoFocus={window.outerWidth > 500}
          fullWidth
          id="new-project-step-one-title"
          name="title"
          type="text"
          placeholder={`e.g. ${placeholders[placeholderIndex]}`}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          helperText={validator.message('title', title, 'required', {
            className: 'text-danger',
          })}
          onKeyDown={handleKeyDown}
          homeDepot={homeDepot}
        />

        <ActionButtons
          secondaryAction={() => setNewProjectStep(0)}
          primaryAction={handleClickNext}
          disabled={disabled}
          loading={loading}
        />
      </div>
    </Slide>
  );
};

export default ProjectTitle;
